<script setup lang="ts">
import * as cheerio from "cheerio"
import PostTextCta from "~/components/blog/PostTextCta.vue"
import PostHeader from "~/components/blog/PostHeader.vue"

interface Props {
  slug: string
  post: {
    header: string
    title: string
    subtitle: string
    date: string
    author: {
      node: {
        firstName: string
        lastName: string
      }
    }
  }
  imageUrl: string
}

const props = defineProps<Props>()

const arePropsWellDefined = computed(() => {
  if (!props.post) {
    return false
  }
  if (!props.post.title && !props.post.subtitle && !props.post.date) {
    return false
  }
  if (!props.post.author && props.post.author.node) {
    return false
  }
  if (!props.post.author.node.firstName && !props.post.author.node.lastName) {
    return false
  }
  if (!props.imageUrl) {
    return false
  }
  return true
})
</script>

<template>
  <div>
    <div v-if="arePropsWellDefined">
      <PostHeader
        :post-title="props.post.title"
        :post-subtitle="props.post.subtitle"
        :post-date="props.post.date"
        :image-url="imageUrl"
        :author-first-name="props.post.author.node.firstName"
        :author-last-name="props.post.author.node.lastName"
      />

      <div class="container is-max-desktop">
        <div class="mx-4">
          <div class="post-container">
            <div id="main-content" class="post-content">
              <!-- eslint-disable vue/no-v-html-->
              <div class="post-html-content" v-html="filteredPostHtml" />
            </div>
            <PostTextCta />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
export default {
  name: "SinglePost",
  computed: {
    filteredPostHtml: function () {
      const postContent = this.post.content
      let modifiedPostContent = this.addContinueReadingLinkAfterIntro(postContent)
      modifiedPostContent = this.addUniqueIdsToHeadings(modifiedPostContent)
      modifiedPostContent = this.removeUnorderedListsFromTableOfContents(modifiedPostContent)

      modifiedPostContent = this.removeInfoboxHeadingFromTableOfContents(modifiedPostContent)
      modifiedPostContent = this.addLinksInTableOfContents(modifiedPostContent)

      modifiedPostContent = this.formatInfoBoxAndRemoveInfoBoxImage(modifiedPostContent)

      return modifiedPostContent
    },
  },
  methods: {
    removeInfoboxHeadingFromTableOfContents(postHtml: string) {
      const $ = cheerio.load(postHtml)

      const headingContents: any[] = []
      $("h2.kt-blocks-info-box-title").each((index, element) => {
        headingContents.push($(element).text())
      })
      if (!headingContents.length) {
        return postHtml
      }

      // remove every table of content item, containing that heading
      headingContents.forEach(headingContent => {
        console.log(headingContent)
        const tableOfContentsEntrySelector = "li > a.kb-table-of-contents__entry"
        const exactTextMatchingListItems = $(tableOfContentsEntrySelector).filter((index, element) => {
          return $(element).text().trim() === headingContent
        })
        exactTextMatchingListItems.parent().remove()
      })
      return $.html()
    },
    addContinueReadingLinkAfterIntro(postHtml: string) {
      // Intro is marked as blockquote. Find it and add the continue-reading-link after it
      const blockQuoteRegex = /<blockquote[\s\S]*<\/blockquote>/g
      const match = postHtml.match(blockQuoteRegex)
      const postContentWithContinueReading = postHtml.replace(
        match,
        `
          ${match}
          <a href="#post-heading-1" class='continue-reading'>Weiterlesen</a>
        `
      )

      // also remove cite tag
      return postContentWithContinueReading.replace(/<cite[\s\S]*<\/cite>/g, "")
    },
    addUniqueIdsToHeadings(postHtml: string) {
      const headingRegex = /<h2 class="wp-block-heading">/g
      const headingMatches = postHtml.match(headingRegex)
      let postHtmlCopy = `${postHtml}`

      if (!headingMatches) {
        return postHtmlCopy
      }
      headingMatches.forEach((match, index) => {
        const headingId = `post-heading-${index + 1}`
        postHtmlCopy = postHtmlCopy.replace(match, `<h2 class="wp-block-heading" id="${headingId}">`)
      })
      return postHtmlCopy
    },
    removeUnorderedListsFromTableOfContents(postHtml: string) {
      const nestedUlsInTableOfContent = /<ul class="kb-table-of-contents-list-sub">[\s\S]*?<\/ul>/g
      return postHtml.replaceAll(nestedUlsInTableOfContent, "")
    },
    addLinksInTableOfContents(postHtml: string) {
      const tableOfContentsLinkRegex = /<a class="kb-table-of-contents__entry" href=".*?"/g
      const linkMatches = postHtml.match(tableOfContentsLinkRegex)
      let postHtmlCopy = `${postHtml}`
      if (!linkMatches) {
        return postHtmlCopy
      }

      linkMatches.forEach((match, index) => {
        const headingId = `post-heading-${index + 1}`
        postHtmlCopy = postHtmlCopy.replace(match, `<a class="kb-table-of-contents__entry" href="#${headingId}"`)
      })
      return postHtmlCopy
    },
    formatInfoBoxAndRemoveInfoBoxImage(postHtml: string) {
      const infoBoxRegex = /<div class="kadence-info-box-icon-inner-container"[\s\S]*?<\/div>/g
      let postHtmlCopy = `${postHtml}`
      postHtmlCopy = postHtmlCopy.replace(infoBoxRegex, "")

      // add icon row div & sub-heading before, surround rest with other div
      const beginningOfInfoHeadline = '<h2 class="kt-blocks-info-box-title'
      const infoBoxParagraphRegexp = /<p class="kt-blocks-info-box-text[\s\S]*?<\/p>/g
      const infoBoxParagraph: RegExpMatchArray | null = postHtmlCopy.match(infoBoxParagraphRegexp)

      const iconRowDiv =
        '<div class="icon-column"></div> <div class="info-box-content-column">' +
        '<div class="info-subheading">INFORMATION</div>'

      postHtmlCopy = postHtmlCopy.replaceAll(
        beginningOfInfoHeadline,
        `
          ${iconRowDiv}
          ${beginningOfInfoHeadline}
        `
      )
      if (infoBoxParagraph === null) {
        return postHtml
      }
      infoBoxParagraph.forEach(paragraph => postHtmlCopy.replaceAll(paragraph, paragraph + "</div>"))
      return postHtmlCopy
    },
  },
}
</script>

<style scoped lang="scss">
body {
  background-color: pink !important;
}

/* Table of Contents */
.table-of-contents-container,
:deep(.wp-block-kadence-tableofcontents) {
  display: flex;
  width: 100%;
  justify-content: flex-start;
}

.post-container {
  padding: 2rem 0 6rem;
  display: grid;
  grid-template-columns: 1fr;
  justify-items: center;
  gap: 24px;

  li {
    margin-left: 0;
  }
}

/* Post-Content */

// Top-Part, Teaser & ToC
.teaser,
.post-html-content :deep(p),
.post-html-content ::v-deep(ul) {
  color: $grey-850;
  font-size: 22px;
  font-weight: 400;
  line-height: 36px;

  @media (max-width: $tablet) {
    font-size: 18px;
  }
}

::v-deep(ul) {
  //background-color: purple;
  color: $grey-850;
  //font-size: 202px;
  font-weight: 400;
  line-height: 36px;
}

::v-deep(blockquote) {
  margin-bottom: 1rem;
}

::v-deep(.continue-reading) {
  font-size: 22px;
  color: $brandpurple-darker;
  font-weight: bolder;
}

::v-deep(.kb-table-of-content-wrap) {
  border-radius: 20px;
  padding: 40px !important; // needed against inline css
  margin-top: 2rem;
  background-color: $sand-50;
  color: $brandpurple-darker;
  max-width: 60%;

  @media (max-width: $tablet) {
    max-width: 100%;
    margin-top: 0;
  }

  .title,
  .kb-table-of-contents-title {
    font-size: 2rem;
    font-weight: 600;
    color: $brandpurple-darker;

    @media (max-width: $tablet) {
      font-size: 22px;
    }
  }

  li {
    list-style: none;
    text-indent: 0rem;

    &:before {
      display: none;
    }

    &:not(:last-child):after {
      display: block;
      content: "";
      border-top: 1px solid #d7cbcb;
      width: 100%;
      margin: 0.5rem 0;
    }

    .kb-table-of-contents__entry {
      font-size: 1.5rem;
      font-weight: normal !important;

      @media (max-width: $tablet) {
        font-size: 20px;
      }
    }

    ul {
      display: none;
    }

    a {
      text-decoration: none;
      color: $brandpurple-darker;

      &:after {
        display: block;
        border-top: 1px solid #d7cbcb;
      }
    }
  }
}

::v-deep(.kt-infobox-textcontent) {
  background-color: $sand-150;
  border-radius: 15px;
  padding: 2.5rem 3rem;
  display: flex;
  max-width: 780px;
  margin: 0 auto;

  .info-subheading {
    font-weight: 400;
    font-size: 1rem;
    line-height: 2rem;
  }

  .icon-column::before {
    content: url("/images/blog/information-icon.svg");
    margin-right: 6rem;
  }

  h2 {
    color: $grey-850;
    margin-top: 0;
    font-size: 1.75rem;
    font-weight: 500;
  }

  @media (max-width: $tablet) {
    padding: 2rem 2rem;
    margin-bottom: 0;
    max-width: 100%;

    .icon-column::before {
      margin-right: 5rem;
    }

    h2 {
      font-size: 1.3rem;
    }
    .kt-blocks-info-box-text {
      margin-bottom: 0;
    }
  }
}

// Images
::v-deep(.post-content) {
  img {
    border-radius: 20px;
    max-height: 400px;
    width: auto !important;
    margin: 1rem auto 0 auto;

    @media (max-width: $tablet) {
      max-height: 300px;
      width: auto;
    }
  }

  figcaption {
    color: #747c90;
    margin-top: 1rem;
    font-size: 18px;
    font-weight: 400;
    line-height: 28px;
  }
}

::v-deep(.kt-has-2-columns) {
  display: flex;

  @media (max-width: $tablet) {
    flex-direction: column;
    padding: 0 !important; // to overwrite inline CSS
  }

  > * {
    flex: 1;
  }

  img {
    max-height: 500px;
    height: auto;
    object-fit: cover;

    @media (max-width: $tablet) {
      max-height: 400px;
      //width: auto;
    }
  }
}

// Headings, Basic Design Elements
::v-deep(p) {
  margin-bottom: 1.5rem;
}

::v-deep(h2) {
  margin-top: 3.5rem;
  font-size: 40px;
  margin-bottom: 1rem;
  color: $brandpurple-darker;

  * {
    margin-top: 3.5rem;
    margin-bottom: 1rem;
    color: $brandpurple-darker;
  }

  @media (max-width: $tablet) {
    font-size: 24px;
  }
}

::v-deep(h3) {
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  font-size: 32px;
  font-weight: 600;
  color: $brandpurple;

  &:before {
    content: "—";
    width: 200px;
    margin-right: 10px;
  }
}

::v-deep(h4) {
  margin-top: 4rem;
  font-size: 32px;
  padding-bottom: 0.5rem;
}

::v-deep(figure) {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 1rem;
}

@media (max-width: $tablet) {
  ::v-deep(h1) {
    font-size: 32px;
  }

  ::v-deep(h2) {
    font-size: 28px;
  }

  ::v-deep(h3) {
    font-size: 24px;
  }

  ::v-deep(figcaption) {
    margin-top: 1rem;
    font-size: 15px !important;
  }
}

// Lists
::v-deep(.post-html-content) > {
  ol,
  ul {
    margin-bottom: 2rem;
  }

  ol {
    counter-reset: item;
    padding-left: 2rem;
    > li {
      counter-increment: item;
      list-style-type: disc;
      list-style-position: inside;
      text-indent: -1em;
      padding-left: 1em;

      &:before {
        content: counter(item) ".";
        font-weight: 600;
        color: $brandpurple;
        display: inline-block;
        width: 1em;
        position: relative;
      }
    }
  }

  ul > li,
  ol > li {
    list-style: none;
    text-indent: -1.3rem;
    margin-left: 0.8rem;

    &:before {
      display: inline-block;
      width: 1em;
      color: $brandpurple;
      position: relative;
    }
  }

  ul > li {
    text-indent: -4rem;
    margin-left: 4.2rem;

    @media (max-width: $tablet) {
      text-indent: -4rem;
      margin-left: 2.5rem;

      &:before {
        width: 3.5rem;
      }
    }

    &:before {
      width: 4rem;
      top: -0.8em;
      left: 3.6rem;
      font-size: 0.5rem;
      content: "⬤";
      padding-left: 2rem;
    }
  }
}

.post-content {
  max-width: 1140px;
}

// links
::v-deep(a) {
  font-weight: 600;
  text-decoration: underline;
}
::v-deep(a:hover) {
  color: $brandpurple-darker;
}

.post-html-content ::v-deep(figcaption) {
  max-width: 680px;
  text-align: center;
  color: #999999;
  font-size: 18px;
}
</style>
